import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Card as CardType } from '../types';
import { cartService, CartItem } from '../services/cartService';

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (card: CardType) => void;
  removeFromCart: (cardId: string) => void;
  updateQuantity: (cardId: string, quantity: number) => void;
  clearCart: () => void;
  resetCart: () => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  isInCart: (cardId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  // Load cart from server on mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        const cartItems = await cartService.loadCart();
        setCartItems(cartItems);
      } catch (error) {
        console.error('Error loading cart:', error);
        setCartItems([]);
      }
    };

    loadCart();
  }, []);

  // Save cart to server whenever it changes
  useEffect(() => {
    if (cartItems.length > 0) {
      cartService.syncCart(cartItems);
    }
  }, [cartItems]);

  const addToCart = (card: CardType) => {
    try {
      // Validate card object before adding
      if (!card || !card.id || typeof card.id !== 'string') {
        console.error('Invalid card object provided to addToCart');
        return;
      }
      
      setCartItems(prevItems => {
        const existingItem = prevItems.find(item => item.card.id === card.id);
        if (existingItem) {
          // If item already exists, increase quantity
          return prevItems.map(item =>
            item.card.id === card.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        } else {
          // Add new item with quantity 1
          return [...prevItems, { card, quantity: 1 }];
        }
      });
    } catch (error) {
      console.error('Error adding item to cart:', error);
      // Clear cart if there's a persistent error
      setCartItems([]);
      localStorage.removeItem('cart');
    }
  };

  const removeFromCart = (cardId: string) => {
    try {
      setCartItems(prevItems => prevItems.filter(item => item.card.id !== cardId));
    } catch (error) {
      console.error('Error removing item from cart:', error);
    }
  };

  const updateQuantity = (cardId: string, quantity: number) => {
    try {
      if (quantity <= 0) {
        removeFromCart(cardId);
        return;
      }
      
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.card.id === cardId
            ? { ...item, quantity }
            : item
        )
      );
    } catch (error) {
      console.error('Error updating cart quantity:', error);
    }
  };

  const clearCart = async () => {
    try {
      setCartItems([]);
      await cartService.clearCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  // Emergency cart reset function
  const resetCart = () => {
    try {
      setCartItems([]);
      localStorage.removeItem('cart');
      console.log('Cart has been reset');
    } catch (error) {
      console.error('Error resetting cart:', error);
    }
  };

  const getCartTotal = () => {
    try {
      return cartItems.reduce((total, item) => {
        const price = typeof item.card.price === 'number' ? item.card.price : 0;
        const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
        return total + (price * quantity);
      }, 0);
    } catch (error) {
      console.error('Error calculating cart total:', error);
      return 0;
    }
  };

  const getCartItemCount = () => {
    try {
      return cartItems.reduce((count, item) => {
        const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
        return count + quantity;
      }, 0);
    } catch (error) {
      console.error('Error calculating cart item count:', error);
      return 0;
    }
  };

  const isInCart = (cardId: string) => {
    try {
      return cartItems.some(item => item.card.id === cardId);
    } catch (error) {
      console.error('Error checking if item is in cart:', error);
      return false;
    }
  };

  const value: CartContextType = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    resetCart,
    getCartTotal,
    getCartItemCount,
    isInCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 